import {
  uniqueId,
  isEmpty,
  orderBy,
  isArray,
} from 'lodash';
import { getValidDate } from '@/utils/date-utils';
import { buildFornecedor } from '@/utils/fornecedor-utils';
import {
  FinalidadeCompra,
  SituacaoCotacao,
  SituacaoRequisicao,
} from 'px-business-components';
import { initialStateCamposErp, computeFrom } from '../produtos/campos-erp/validate-campos-erp';

const SITUACOES_INVALIDAS = [
  SituacaoRequisicao.keys.CANCELADA.key,
  SituacaoRequisicao.keys.FINALIZADA.key,
];
const TEMP_KEYS = ['$$id', '$$hasFileLoading'];

const checkIsProdutoValido = situacao => !SITUACOES_INVALIDAS.includes(situacao);

export const initialStateProduto = (data, erpCamposIntegracao) => ({
  $$id: uniqueId(),
  produto: null,
  quantidade: 0,
  unidadeMedida: null,
  especificacaoProduto: null,
  arquivos: [],
  observacaoInterna: null,
  observacaoExterna: null,
  projeto: data?.projeto ?? null,
  centroCusto: data?.centroCusto ?? null,
  finalidadeCompra: data?.finalidadeCompra ?? FinalidadeCompra.keys.USO_CONSUMO.key,
  dataPrevisaoEntrega: data?.prazoEntrega ?? null,
  cadastrosAdicionais: initialStateCamposErp(erpCamposIntegracao),
  $$hasFileLoading: false,
});

export const initialState = erpCamposIntegracao => ({
  identificador: null,
  comprador: null,
  locaisEntrega: null,
  tipoFrete: null,
  prazoEntrega: null,
  centroCusto: null,
  projeto: null,
  observacao: null,
  observacaoInterna: null,
  validade: null,
  finalidadeCompra: FinalidadeCompra.keys.USO_CONSUMO.key,
  situacao: SituacaoCotacao.keys.ABERTA.key,
  tipoCotacao: 'COTACAO_ABERTA',
  formaPagamento: null,
  prazoPagamento: null,
  produtos: [
    initialStateProduto(null, erpCamposIntegracao),
  ],
  fornecedores: [],
  arquivos: [],
});

export const computeInitialStateProduto = (produto, erpCamposIntegracao) => {
  if (!produto) {
    return initialStateProduto(erpCamposIntegracao);
  }

  const computedProduto = { ...initialStateProduto(erpCamposIntegracao) };

  computedProduto.id = produto.id ? produto.id : undefined;

  Object.keys(computedProduto)
    .filter(key => !TEMP_KEYS.includes(key))
    .forEach((key) => {
      computedProduto[key] = produto[key];
    });

  computedProduto.cadastrosAdicionais = computeFrom(
    erpCamposIntegracao,
    produto.cadastrosAdicionais,
  );

  if (isEmpty(computedProduto.arquivos)) {
    computedProduto.arquivos = produto.produto?.arquivos ?? [];
  }

  return computedProduto;
};

export const computeInitialStateCopiaProduto = (produto, erpCamposIntegracao) => {
  const computedProduto = { ...initialStateProduto(erpCamposIntegracao) };
  Object.keys(computedProduto)
    .filter(key => !TEMP_KEYS.includes(key))
    .forEach((key) => {
      computedProduto[key] = produto[key];
    });
  computedProduto.dataPrevisaoEntrega = null;
  computedProduto.cadastrosAdicionais = computeFrom(
    erpCamposIntegracao,
    produto.cadastrosAdicionais,
  );
  if (isEmpty(computedProduto.arquivos)) {
    computedProduto.arquivos = produto.produto?.arquivos ?? [];
  }

  return computedProduto;
};

export const copyProduto = (produto, data, erpCamposIntegracao) => {
  const computedProduto = { ...initialStateProduto(erpCamposIntegracao) };

  Object.keys(computedProduto)
    .filter(key => !TEMP_KEYS.includes(key))
    .forEach((key) => {
      computedProduto[key] = produto[key];
    });

  computedProduto.id = produto.id ? produto.id : undefined;
  computedProduto.dataPrevisaoEntrega = produto.dataPrevisaoEntrega ?? data?.prazoEntrega ?? null;
  computedProduto.projeto = produto.projeto ?? data?.projeto ?? null;
  computedProduto.centroCusto = produto.centroCusto ?? data?.centroCusto ?? null;
  computedProduto.finalidadeCompra = produto.finalidadeCompra ?? data?.finalidadeCompra ?? null;
  computedProduto.requisicaoProduto = produto.requisicaoProduto?.id
    ? { id: produto.requisicaoProduto?.id } : undefined;

  computedProduto.cadastrosAdicionais = isArray(produto.cadastrosAdicionais) ? computeFrom(
    erpCamposIntegracao,
    produto.cadastrosAdicionais,
  ) : produto.cadastrosAdicionais;
  if (isEmpty(computedProduto.arquivos)) {
    computedProduto.arquivos = produto.produto?.arquivos ?? [];
  }
  return computedProduto;
};

export const computeInitialCopia = (data, erpCamposIntegracao) => {
  const copia = { ...initialState(erpCamposIntegracao) };

  Object.keys(copia)
    .forEach((key) => {
      copia[key] = data[key];
    });

  copia.situacao = SituacaoCotacao.keys.ABERTA.key;
  copia.prazoEntrega = null;
  copia.validade = null;
  copia.identificador = undefined;

  copia.produtos = copia.produtos.map(p => ({
    ...computeInitialStateCopiaProduto(p, erpCamposIntegracao),
  }));

  if (!isEmpty(copia.fornecedores)) {
    copia.fornecedores = copia.fornecedores.map(f => buildFornecedor(data, f));
  }

  return copia;
};

export const computeInitialRascunho = (data, erpCamposIntegracao) => {
  const rascunho = { ...initialState(erpCamposIntegracao) };

  Object.keys(rascunho)
    .forEach((key) => {
      rascunho[key] = data[key];
    });

  rascunho.id = data.id;

  if (!isEmpty(rascunho.produtos)) {
    rascunho.produtos = rascunho.produtos.map(p => ({
      ...copyProduto(p, data, erpCamposIntegracao),
    }));
  } else {
    rascunho.produtos = [initialStateProduto(data, erpCamposIntegracao)];
  }

  if (!isEmpty(rascunho.fornecedores)) {
    rascunho.fornecedores = rascunho.fornecedores.map(f => buildFornecedor(data, f));
  }

  return rascunho;
};

export const computeInitialRequisicaoCotacaoProduto = (produto, data, erpCamposIntegracao) => {
  const computedProduto = { ...initialStateProduto(null, erpCamposIntegracao) };
  Object.keys(computedProduto)
    .filter(key => !TEMP_KEYS.includes(key))
    .forEach((key) => {
      computedProduto[key] = produto[key];
    });

  computedProduto.requisicaoProduto = produto.requisicaoProduto;
  computedProduto.centroCusto = produto.centroCusto ?? data.centroCusto ?? null;
  computedProduto.projeto = produto.projeto ?? data.projeto ?? null;
  computedProduto.finalidadeCompra = produto.finalidadeCompra ?? data.finalidadeCompra ?? null;
  computedProduto.dataPrevisaoEntrega = getValidDate(
    produto.dataPrevisaoEntrega,
    data.prazoEntrega,
  );
  computedProduto.cadastrosAdicionais = computeFrom(
    erpCamposIntegracao,
    produto.requisicaoProduto.cadastrosAdicionais,
  );

  if (isEmpty(computedProduto.arquivos)) {
    computedProduto.arquivos = produto.produto?.arquivos ?? [];
  }

  return computedProduto;
};

export const createFromRequisicao = (data, erpCamposIntegracao) => {
  const cotacao = { ...initialState(erpCamposIntegracao) };
  const prazoEntregaValido = getValidDate(data.prazoEntrega);

  Object.keys(cotacao)
    .forEach((key) => {
      cotacao[key] = data[key];
    });
  cotacao.prazoEntrega = prazoEntregaValido;
  cotacao.identificador = data.identificador ?? undefined;

  const validProdutos = cotacao.produtos
    .filter(produto => checkIsProdutoValido(produto.requisicaoProduto.situacao))
    .map(produto => ({
      ...computeInitialRequisicaoCotacaoProduto(produto, cotacao, erpCamposIntegracao),
    }));

  cotacao.produtos = orderBy(validProdutos, ['requisicaoProduto.id'], ['asc']);

  if (!isEmpty(cotacao.fornecedores)) {
    cotacao.fornecedores = cotacao.fornecedores.map(f => buildFornecedor(data, f));
  }
  return cotacao;
};

export default {
  initialState,
  initialStateProduto,
};
